import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  AlertTriangle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  FileDown,
  FileSpreadsheet,
  FileStack,
  Info,
  LayoutTemplate,
  Loader2,
  Plus,
  Sparkles,
  UploadCloud,
  X,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { getOrganizationTranslation } from "../context/organizationOnboardingTranslations";

const API_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";

const decodeToken = (token) => {
  try {
    const payload = token?.split(".")?.[1];
    if (!payload) return null;
    const normalized = payload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(payload.length / 4) * 4, "=");
    return JSON.parse(atob(normalized));
  } catch {
    return null;
  }
};

const BulkCreate = () => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const templateTr = (key, fallback) =>
    t?.ui?.templatePolicy?.[key] ||
    getOrganizationTranslation(lang, "templatePolicy", key, fallback);

  const [formData, setFormData] = useState({
    theme: "",
    subTheme: "",
    start_date: "",
    end_date: "",
    templateModel: "cb4c",
  });
  const [dateMode, setDateMode] = useState("range");
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedDateInput, setSelectedDateInput] = useState("");
  const [file, setFile] = useState(null);
  const [themes, setThemes] = useState({});
  const [loadingThemes, setLoadingThemes] = useState(true);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const [templatePolicy, setTemplatePolicy] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [status, setStatus] = useState({
    loading: false,
    type: "",
    message: "",
    details: [],
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }

    let storedUser = null;
    try {
      storedUser = JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      storedUser = null;
    }

    const tokenData = decodeToken(token);
    const userRole = tokenData?.type || storedUser?.type;

    // Permission conservée : cette page reste réservée au rôle Client.
    if (userRole !== "Client") {
      navigate("/unauthorized", { replace: true });
      return;
    }

    setCheckingAuth(false);
  }, [navigate]);

  useEffect(() => {
    const fetchThemes = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}/api/certifs/themes`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        const themesPayload = data?.themes ?? data?.data ?? data;

        setThemes(
          themesPayload &&
            typeof themesPayload === "object" &&
            !Array.isArray(themesPayload)
            ? themesPayload
            : {}
        );
      } catch (error) {
        console.error("Unable to load certificate themes:", error);
        setStatus({
          loading: false,
          type: "error",
          message:
            t?.loadingThemesError ||
            "Impossible de charger les thèmes de certificats.",
          details: [],
        });
      } finally {
        setLoadingThemes(false);
      }
    };

    fetchThemes();
  }, [t]);

  useEffect(() => {
    const fetchTemplatePolicy = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}/api/organizations/current/template-policy`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.message || templateTr("templateLoadError", "Impossible de charger le modèle de certificat."));
        }
        setTemplatePolicy(data);
        if (data?.mode === "single") {
          setFormData((current) => ({ ...current, templateModel: "organization" }));
        }
      } catch (error) {
        console.error("Unable to load template policy:", error);
        setStatus({
          loading: false,
          type: "error",
          message: error.message || templateTr("templateLoadError", "Impossible de charger le modèle de certificat."),
          details: [],
        });
      } finally {
        setLoadingTemplate(false);
      }
    };
    fetchTemplatePolicy();
  }, []);

  const sortedSelectedDates = useMemo(
    () => [...selectedDates].sort(),
    [selectedDates]
  );
  const derivedStartDate = sortedSelectedDates[0] || "";
  const derivedEndDate = sortedSelectedDates.at(-1) || "";
  const themeNames = useMemo(
    () =>
      themes && typeof themes === "object" && !Array.isArray(themes)
        ? Object.keys(themes)
        : [],
    [themes]
  );

  const availableSubThemes = useMemo(() => {
    if (!formData.theme) return [];

    const themeValue = themes?.[formData.theme];

    // Format actuel du backend : { "Sous-thème": durée }
    if (
      themeValue &&
      typeof themeValue === "object" &&
      !Array.isArray(themeValue)
    ) {
      return Object.keys(themeValue);
    }

    // Compatibilité avec une éventuelle réponse sous forme de tableau.
    if (Array.isArray(themeValue)) {
      return themeValue
        .map((item) => {
          if (typeof item === "string") return item;
          if (!item || typeof item !== "object") return "";
          return item.name || item.label || item.title || item.subTheme || "";
        })
        .filter(Boolean);
    }

    return [];
  }, [formData.theme, themes]);

  const formatDate = (date) => {
    if (!date) return "—";
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(`${date}T00:00:00`));
  };

  const downloadTemplate = () => {
    const csv = isSingleTemplate
      ? [
          "firstName,lastName,email",
          "Ahmed,Ben Ali,ahmed@example.com",
          "Sarra,Trabelsi,sarra@example.com",
        ].join("\n")
      : [
          "firstName,lastName,email,success",
          "Ahmed,Ben Ali,ahmed@example.com,true",
          "Sarra,Trabelsi,sarra@example.com,true",
        ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "participants_template.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const addSelectedDate = () => {
    if (!selectedDateInput || selectedDates.includes(selectedDateInput)) {
      setSelectedDateInput("");
      return;
    }
    setSelectedDates((current) => [...current, selectedDateInput].sort());
    setSelectedDateInput("");
  };

  const removeSelectedDate = (date) => {
    setSelectedDates((current) => current.filter((item) => item !== date));
  };

  const isSingleTemplate = templatePolicy?.mode === "single";
  const hasOrganizationTemplate = !isSingleTemplate || Boolean(templatePolicy?.template?._id);
  const selectedTemplateLabel = isSingleTemplate
    ? templatePolicy?.template?.name || templateTr("organizationModel", "Modèle officiel de l’organisation")
    : formData.templateModel === "cb4c"
      ? "CB4C"
      : "CertiLink";

  const handleSubmit = async (event) => {
    event.preventDefault();
    const rangeMode = dateMode === "range";

    if (!hasOrganizationTemplate) {
      setStatus({
        loading: false,
        type: "error",
        message: templateTr("missingTemplate", "Aucun modèle PDF actif n’est configuré pour cette organisation."),
        details: [],
      });
      return;
    }

    if (
      !file ||
      !formData.theme ||
      !formData.subTheme ||
      (rangeMode && (!formData.start_date || !formData.end_date)) ||
      (!rangeMode && sortedSelectedDates.length === 0)
    ) {
      setStatus({
        loading: false,
        type: "error",
        message:
          t?.errorFillFields ||
          "Complétez la formation, les dates et le fichier CSV.",
        details: [],
      });
      return;
    }

    if (
      rangeMode &&
      new Date(formData.end_date) < new Date(formData.start_date)
    ) {
      setStatus({
        loading: false,
        type: "error",
        message: "La date de fin doit être postérieure à la date de début.",
        details: [],
      });
      return;
    }

    const payload = new FormData();
    payload.append("file", file);
    payload.append("theme", formData.theme);
    payload.append("subTheme", formData.subTheme);
    payload.append("date_mode", dateMode);
    payload.append("templateModel", formData.templateModel);

    if (rangeMode) {
      payload.append("start_date", formData.start_date);
      payload.append("end_date", formData.end_date);
    } else {
      payload.append("selected_dates", sortedSelectedDates.join(","));
      payload.append("start_date", derivedStartDate);
      payload.append("end_date", derivedEndDate);
    }

    setStatus({ loading: true, type: "", message: "", details: [] });

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/certifs/bulk-create`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setStatus({
        loading: false,
        type: "success",
        message:
          response.data?.message ||
          t?.importSuccess ||
          "Les certificats ont été générés avec succès.",
        details: response.data?.errors || [],
      });
      setFile(null);
    } catch (error) {
      setStatus({
        loading: false,
        type: "error",
        message:
          error.response?.data?.message ||
          t?.serverError ||
          "Une erreur est survenue pendant l'importation.",
        details: error.response?.data?.errors || [],
      });
    }
  };

  if (checkingAuth || loadingThemes || loadingTemplate) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-bold text-slate-700 shadow-sm">
          <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
          {checkingAuth
            ? t?.checkingPermissions || "Vérification des permissions..."
            : loadingThemes
              ? t?.loadingThemes || "Chargement des thèmes..."
              : templateTr("loadingTemplate", "Chargement du modèle de certificat...")}
        </div>
      </div>
    );
  }

  const fieldClass =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400";

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate("/participants")}
              className="mb-4 inline-flex items-center gap-2 text-sm font-bold text-slate-500 transition hover:text-blue-700"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour aux participants
            </button>
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 text-white shadow-lg shadow-blue-600/20">
                <FileStack className="h-6 w-6" />
              </span>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">
                  Création multiple
                </p>
                <h1 className="mt-1 text-3xl font-black tracking-tight text-slate-950">
                  {t?.bulkCreateTitle || "Importer et générer en masse"}
                </h1>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={downloadTemplate}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-bold text-blue-700 transition hover:bg-blue-100"
          >
            <FileDown className="h-4 w-4" />
            {t?.downloadTemplateBtn || "Télécharger le modèle CSV"}
          </button>
        </div>

        <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
          <form
            onSubmit={handleSubmit}
            className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/50"
          >
            <div className="border-b border-slate-100 bg-gradient-to-r from-slate-950 to-blue-950 px-6 py-6 text-white sm:px-8">
              <div className="flex items-start gap-4">
                <span className="rounded-2xl bg-white/10 p-3 text-cyan-300">
                  <Sparkles className="h-6 w-6" />
                </span>
                <div>
                  <h2 className="text-xl font-black">Configurer la génération</h2>
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
                    Sélectionnez la formation et sa période, puis importez les
                    participants depuis un fichier CSV.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-8 p-6 sm:p-8">
              <section>
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-600 text-sm font-black text-white">
                    1
                  </span>
                  <div>
                    <h3 className="font-black text-slate-950">Formation</h3>
                    <p className="text-xs text-slate-500">
                      Thème, sous-thème et modèle de certificat
                    </p>
                  </div>
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <label className="text-sm font-bold text-slate-700">
                    {t?.mainThemeLabel || "Thème principal"}
                    <select
                      value={formData.theme}
                      onChange={(event) =>
                        setFormData((current) => ({
                          ...current,
                          theme: event.target.value,
                          subTheme: "",
                        }))
                      }
                      required
                      className={`${fieldClass} mt-2`}
                    >
                      <option value="">
                        {t?.selectThemePlaceholder || "Sélectionner un thème"}
                      </option>
                      {themeNames.map((theme) => (
                        <option key={theme} value={theme}>
                          {theme}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="text-sm font-bold text-slate-700">
                    {t?.subThemeLabel || "Sous-thème"}
                    <select
                      value={formData.subTheme}
                      onChange={(event) =>
                        setFormData((current) => ({
                          ...current,
                          subTheme: event.target.value,
                        }))
                      }
                      disabled={!formData.theme}
                      required
                      className={`${fieldClass} mt-2`}
                    >
                      <option value="">Sélectionner un sous-thème</option>
                      {availableSubThemes.map((subTheme) => (
                        <option key={subTheme} value={subTheme}>
                          {subTheme}
                        </option>
                      ))}
                    </select>
                  </label>

                  <fieldset className="md:col-span-2">
                    <legend className="inline-flex items-center gap-2 text-sm font-bold text-slate-700">
                      <LayoutTemplate className="h-4 w-4 text-slate-400" />
                      {t?.selectModelLabel || (lang === "en" ? "Certificate template" : "Modèle de certificat")}
                    </legend>

                    {isSingleTemplate ? (
                      <div className={`mt-3 rounded-2xl border p-4 ${
                        hasOrganizationTemplate
                          ? "border-blue-400 bg-blue-50 ring-2 ring-blue-100"
                          : "border-rose-200 bg-rose-50"
                      }`}>
                        <span className="flex items-start gap-3">
                          <span className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                            hasOrganizationTemplate ? "bg-blue-600 text-white" : "bg-rose-100 text-rose-700"
                          }`}>
                            <LayoutTemplate className="h-5 w-5" />
                          </span>
                          <span className="min-w-0">
                            <span className="flex items-center gap-2 font-black text-slate-900">
                              {selectedTemplateLabel}
                              {hasOrganizationTemplate && <CheckCircle2 className="h-4 w-4 text-blue-600" />}
                            </span>
                            <span className="mt-1 block text-xs leading-5 text-slate-500">
                              {hasOrganizationTemplate
                                ? templatePolicy?.template?.originalFilename ||
                                  templateTr("organizationModelDescription", "Ce modèle PDF unique appartient à l’organisation.")
                                : templateTr("missingTemplate", "Aucun modèle PDF actif n’est configuré pour cette organisation.")}
                            </span>
                          </span>
                        </span>
                      </div>
                    ) : (
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        {[
                          {
                            value: "cb4c",
                            title: "CB4C",
                            description: lang === "en" ? "CB4C institutional template" : "Modèle institutionnel CB4C",
                          },
                          {
                            value: "certilink",
                            title: "CertiLink",
                            description: lang === "en" ? "Official CertiLink template" : "Modèle officiel CertiLink",
                          },
                        ].map((model) => {
                          const selected = formData.templateModel === model.value;
                          return (
                            <label
                              key={model.value}
                              className={`group cursor-pointer rounded-2xl border p-4 transition-all ${
                                selected
                                  ? "border-blue-400 bg-blue-50 ring-2 ring-blue-100"
                                  : "border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50"
                              }`}
                            >
                              <input
                                type="radio"
                                name="templateModel"
                                value={model.value}
                                checked={selected}
                                onChange={(event) =>
                                  setFormData((current) => ({
                                    ...current,
                                    templateModel: event.target.value,
                                  }))
                                }
                                className="sr-only"
                              />
                              <span className="flex items-start gap-3">
                                <span className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition ${
                                  selected
                                    ? "bg-blue-600 text-white"
                                    : "bg-slate-100 text-slate-500 group-hover:bg-blue-100 group-hover:text-blue-700"
                                }`}>
                                  <LayoutTemplate className="h-5 w-5" />
                                </span>
                                <span className="min-w-0">
                                  <span className="flex items-center gap-2 font-black text-slate-900">
                                    {model.title}
                                    {selected && <CheckCircle2 className="h-4 w-4 text-blue-600" />}
                                  </span>
                                  <span className="mt-1 block text-xs leading-5 text-slate-500">
                                    {model.description}
                                  </span>
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </fieldset>
                </div>
              </section>

              <section className="border-t border-slate-100 pt-8">
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-cyan-600 text-sm font-black text-white">
                    2
                  </span>
                  <div>
                    <h3 className="font-black text-slate-950">Période</h3>
                    <p className="text-xs text-slate-500">
                      Intervalle continu ou jours spécifiques
                    </p>
                  </div>
                </div>

                <div className="mb-5 grid gap-3 sm:grid-cols-2">
                  {[
                    ["range", "Période continue", "Date de début et date de fin"],
                    ["days", "Jours spécifiques", "Sélection manuelle des journées"],
                  ].map(([value, title, subtitle]) => (
                    <label
                      key={value}
                      className={`cursor-pointer rounded-2xl border p-4 transition ${
                        dateMode === value
                          ? "border-blue-300 bg-blue-50 ring-2 ring-blue-100"
                          : "border-slate-200 bg-white hover:border-blue-200"
                      }`}
                    >
                      <input
                        type="radio"
                        name="dateMode"
                        value={value}
                        checked={dateMode === value}
                        onChange={(event) => setDateMode(event.target.value)}
                        className="sr-only"
                      />
                      <p className="font-black text-slate-900">{title}</p>
                      <p className="mt-1 text-xs text-slate-500">{subtitle}</p>
                    </label>
                  ))}
                </div>

                {dateMode === "range" ? (
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label className="text-sm font-bold text-slate-700">
                      {t?.startDateLabel || "Date de début"}
                      <input
                        type="date"
                        value={formData.start_date}
                        onChange={(event) =>
                          setFormData((current) => ({
                            ...current,
                            start_date: event.target.value,
                          }))
                        }
                        required
                        className={`${fieldClass} mt-2`}
                      />
                    </label>
                    <label className="text-sm font-bold text-slate-700">
                      {t?.endDateLabel || "Date de fin"}
                      <input
                        type="date"
                        value={formData.end_date}
                        onChange={(event) =>
                          setFormData((current) => ({
                            ...current,
                            end_date: event.target.value,
                          }))
                        }
                        required
                        className={`${fieldClass} mt-2`}
                      />
                    </label>
                  </div>
                ) : (
                  <div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <label className="flex-1 text-sm font-bold text-slate-700">
                        Ajouter une journée
                        <input
                          type="date"
                          value={selectedDateInput}
                          onChange={(event) =>
                            setSelectedDateInput(event.target.value)
                          }
                          className={`${fieldClass} mt-2`}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={addSelectedDate}
                        className="mt-auto inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-5 py-3 text-sm font-bold text-blue-700 transition hover:bg-blue-100"
                      >
                        <Plus className="h-4 w-4" />
                        Ajouter
                      </button>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {sortedSelectedDates.length ? (
                        sortedSelectedDates.map((date) => (
                          <span
                            key={date}
                            className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-xs font-bold text-cyan-800"
                          >
                            {formatDate(date)}
                            <button
                              type="button"
                              aria-label={`Retirer ${date}`}
                              onClick={() => removeSelectedDate(date)}
                              className="rounded-full p-0.5 hover:bg-cyan-100"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        ))
                      ) : (
                        <p className="text-xs text-slate-500">
                          Aucun jour sélectionné.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </section>

              <section className="border-t border-slate-100 pt-8">
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-600 text-sm font-black text-white">
                    3
                  </span>
                  <div>
                    <h3 className="font-black text-slate-950">Participants CSV</h3>
                    <p className="text-xs text-slate-500">
                      Importez les participants à certifier
                    </p>
                  </div>
                </div>

                <label
                  className={`relative flex min-h-52 cursor-pointer flex-col items-center justify-center rounded-3xl border-2 border-dashed p-7 text-center transition ${
                    file
                      ? "border-emerald-300 bg-emerald-50"
                      : "border-slate-300 bg-slate-50 hover:border-blue-300 hover:bg-blue-50/50"
                  }`}
                >
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(event) => setFile(event.target.files?.[0] || null)}
                    className="sr-only"
                  />
                  {file ? (
                    <>
                      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-lg shadow-emerald-600/20">
                        <CheckCircle2 className="h-7 w-7" />
                      </span>
                      <p className="mt-4 max-w-full truncate font-black text-emerald-900">
                        {file.name}
                      </p>
                      <p className="mt-1 text-xs font-semibold text-emerald-700">
                        {(file.size / 1024).toFixed(1)} Ko · prêt à être importé
                      </p>
                    </>
                  ) : (
                    <>
                      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-blue-600 shadow-sm ring-1 ring-slate-200">
                        <UploadCloud className="h-7 w-7" />
                      </span>
                      <p className="mt-4 font-black text-slate-900">
                        Cliquez pour choisir un fichier CSV
                      </p>
                      <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500">
                        {isSingleTemplate
                          ? templateTr(
                              "bulkAllSuccessfulColumns",
                              lang === "fr"
                                ? "Colonnes conseillées : firstName, lastName et email. Tous les certificats seront enregistrés comme réussite."
                                : "Recommended columns: firstName, lastName and email. All certificates will be recorded as successful."
                            )
                          : lang === "fr"
                            ? "Colonnes conseillées : firstName, lastName, email et success."
                            : "Recommended columns: firstName, lastName, email and success."}
                      </p>
                    </>
                  )}
                </label>
              </section>

              {status.message && (
                <div
                  className={`flex items-start gap-3 rounded-2xl border p-4 text-sm font-semibold ${
                    status.type === "success"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "border-rose-200 bg-rose-50 text-rose-800"
                  }`}
                >
                  {status.type === "success" ? (
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                  ) : (
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                  )}
                  <div>
                    <p>{status.message}</p>
                    {status.details?.length > 0 && (
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                        {status.details.map((detail, index) => (
                          <li key={`${index}-${JSON.stringify(detail)}`}>
                            {typeof detail === "string"
                              ? detail
                              : detail.message ||
                                detail.email ||
                                JSON.stringify(detail)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={status.loading || !hasOrganizationTemplate}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-3.5 text-sm font-black text-white shadow-lg shadow-blue-600/20 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status.loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <FileStack className="h-5 w-5" />
                )}
                {status.loading
                  ? t?.processing || "Génération en cours..."
                  : t?.bulkCreateBtn || "Lancer la création multiple"}
              </button>
            </div>
          </form>

          <aside className="space-y-5 xl:sticky xl:top-24 xl:self-start">
            <div className="rounded-[2rem] bg-slate-950 p-6 text-white shadow-xl">
              <div className="flex items-center gap-3">
                <CalendarDays className="h-5 w-5 text-cyan-300" />
                <h2 className="font-black">Résumé</h2>
              </div>
              <div className="mt-6 space-y-4">
                {[
                  ["Thème", formData.theme || "Non sélectionné"],
                  [lang === "en" ? "Sub-theme" : "Sous-thème", formData.subTheme || (lang === "en" ? "Not selected" : "Non sélectionné")],
                  [templateTr("model", lang === "en" ? "Template" : "Modèle"), selectedTemplateLabel],
                  [
                    "Période",
                    dateMode === "range"
                      ? formData.start_date && formData.end_date
                        ? `${formatDate(formData.start_date)} → ${formatDate(
                            formData.end_date
                          )}`
                        : "Non définie"
                      : sortedSelectedDates.length
                      ? `${sortedSelectedDates.length} jour(s)`
                      : "Non définie",
                  ],
                  ["Fichier", file?.name || "Aucun fichier"],
                ].map(([label, value]) => (
                  <div key={label} className="border-b border-white/10 pb-4 last:border-0 last:pb-0">
                    <p className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">
                      {label}
                    </p>
                    <p className="mt-1 break-words text-sm font-bold text-slate-100">
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[2rem] border border-blue-100 bg-blue-50 p-6">
              <div className="flex items-center gap-3 text-blue-800">
                <Info className="h-5 w-5" />
                <h2 className="font-black">Bonnes pratiques</h2>
              </div>
              <ul className="mt-4 space-y-3 text-sm leading-6 text-blue-900/80">
                {[
                  "Conservez une seule ligne par participant.",
                  "Vérifiez les adresses email avant l’import.",
                  "Utilisez le modèle fourni pour éviter les erreurs de colonnes.",
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-blue-600" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <button
              type="button"
              onClick={downloadTemplate}
              className="flex w-full items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-black text-slate-800 shadow-sm transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
            >
              <FileSpreadsheet className="h-5 w-5" />
              Modèle CSV
            </button>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default BulkCreate;
